import { FunctionComponent, ReactNode, useCallback, useEffect } from "react";
import {
  RefineThemedLayoutV2SiderProps,
  ThemedLayoutV2,
  ThemedSiderV2,
} from "@refinedev/mui";
import Box from "@mui/material/Box";
import { useKbarActions } from "../../hooks/useKbarActions";
import { ThemedKbar } from "./ThemedKbar";
import {
  Header,
  HeaderWithTitle,
  HeaderWithTitleAndMarketplace,
} from "./Header";
import { TitleProps } from "@refinedev/core";
import { useInitializeApp } from "../../hooks/useInitializeApp";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReAuthentication } from "../../pages/authentication/ReAuthentication";
import { ReAuthenticationCallback } from "../../pages/authentication/ReAuthenticationCallback";
import { SyncrosaleThemedTitle } from "./SyncrosaleThemedTitle";
import { SyncrosaleLoader } from "./components/SyncrosaleLoader";
import { getRuntimeEnv } from "../../providers/getRuntimeEnv";

interface SyncrosaleLayoutProps {
  children: ReactNode;
}

// useRefineKbar bilerek çağrılmıyor: resource'lardan otomatik üretilen
// "... listele" aksiyonları yerine useKbarActions'taki özel aksiyonlar kullanılıyor.
const KbarSetup = () => {
  useKbarActions();
  return null;
};

// Removes logout button from sidebar — the account menu itself lives in the
// header. The collapse/expand toggle also lives in the header (far left,
// see Header.tsx), not here — ThemedSiderV2's own built-in one is hidden via
// CSS since it only ever collapses (no way back).
const Sider = (props: RefineThemedLayoutV2SiderProps) => {
  return (
    <ThemedSiderV2
      {...props}
      render={({ items, collapsed }) => (
        <Box className={collapsed ? "sider-collapsed" : undefined}>
          {items}
        </Box>
      )}
    />
  );
};

export const SyncrosaleLayout = ({ children }: SyncrosaleLayoutProps) => {
  const { showLoading, hasStore, needsReAuthentication, invalidMarketplace } =
    useInitializeApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const runtimeEnv = getRuntimeEnv();
  const isDevEnv = runtimeEnv === "dev";
  const title = isDevEnv ? "SyncroDev" : "Syncrosale";
  const logo = isDevEnv ? "/icons/logo-dev.svg" : "/icons/logo.svg";

  const renderTitle: FunctionComponent<TitleProps> = useCallback(
    (props) => <SyncrosaleThemedTitle {...props} text={title} icon={logo} />,
    [title, logo],
  );

  const isReAuthCallback =
    searchParams.get("spapi_oauth_code") &&
    localStorage.getItem("reAuthenticating") === "true";

  useEffect(() => {
    if (
      !hasStore &&
      !showLoading &&
      !needsReAuthentication &&
      window.location.pathname !== "/store/create" &&
      !isReAuthCallback
    ) {
      setTimeout(() => navigate("/store/create"), 100);
    }
  }, [
    hasStore,
    navigate,
    showLoading,
    needsReAuthentication,
    isReAuthCallback,
  ]);

  if (showLoading) {
    return <SyncrosaleLoader />;
  }

  if (isReAuthCallback) {
    return <ReAuthenticationCallback />;
  }

  if (window.location.pathname === "/store/create") {
    return (
      <ThemedLayoutV2
        Header={HeaderWithTitleAndMarketplace}
        Title={renderTitle}
        Sider={() => null}
      >
        {children}
      </ThemedLayoutV2>
    );
  }

  if (needsReAuthentication && invalidMarketplace) {
    return (
      <ThemedLayoutV2
        Header={HeaderWithTitleAndMarketplace}
        Title={renderTitle}
        Sider={() => null}
      >
        <ReAuthentication invalidMarketplaces={[invalidMarketplace]} />
      </ThemedLayoutV2>
    );
  }

  return (
    <>
      <ThemedKbar />
      <KbarSetup />
      <ThemedLayoutV2
        Header={hasStore ? Header : HeaderWithTitle}
        Title={renderTitle}
        Sider={hasStore ? Sider : () => null}
      >
        {children}
      </ThemedLayoutV2>
    </>
  );
};
